import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { captureError } from '../monitoring';

import { ErrorState } from './error-state';

export interface FeatureErrorBoundaryProps {
  children: ReactNode;
  /** Identifies the feature in monitoring. */
  name: string;
  fallback?: (error: unknown, reset: () => void) => ReactNode;
}

interface State {
  error: unknown;
}

class Boundary extends Component<FeatureErrorBoundaryProps & { onReset: () => void }, State> {
  override state: State = { error: undefined };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    captureError(error, { feature: this.props.name, componentStack: info.componentStack });
  }

  reset = () => {
    this.props.onReset();
    this.setState({ error: undefined });
  };

  override render() {
    if (this.state.error === undefined) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
    return <ErrorState error={this.state.error} onRetry={this.reset} />;
  }
}

/**
 * Wrap every feature page. Query errors thrown via `throwOnError` and render errors land here,
 * render inline, and can be retried (which also resets the failed queries underneath).
 */
export function FeatureErrorBoundary(props: FeatureErrorBoundaryProps) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => <Boundary {...props} onReset={reset} />}
    </QueryErrorResetBoundary>
  );
}
